/**
 * Generic "default" card body — used for every product that doesn't
 * match any of the specialised category branches (i.e. NOT Laminate /
 * Hardware / Hardware System / Veneer / thickness-bulk / weight-bulk).
 *
 * Renders three sub-blocks:
 *   1. Variant list preview (up to 3 thicknesses + "+N more variants")
 *   2. Single-variant → one big Add button, OR
 *   3. Multi-variant  → up to 3 outline buttons, one per variant, each
 *      with its own rate.
 *
 * Extracted from ProductsPage.js. Pure JSX. The
 * caller picks which sub-block to show based on `variants.length`.
 */
import React from 'react';
import { Button } from '../../../components/ui/button';
import { Plus } from 'lucide-react';
import { formatINR } from '../../../lib/store';


export function DefaultCardVariantsPreview({ group }) {
  return (
    <div className="space-y-1 flex-1">
      {group.variants.slice(0, 3).map((v, i) => (
        <div key={i} className="flex justify-between items-center text-sm">
          <span className="text-heda-sub">
            {v.thickness || '-'}
            {v.size && v.size !== '8x4' && (
              <span className="text-xs ml-1 text-heda-mute">({v.size})</span>
            )}
          </span>
          <span className="font-mono-data font-semibold text-heda-olive-dark">
            {formatINR(v.base_rate)}
          </span>
        </div>
      ))}
      {group.variants.length > 3 && (
        <p className="text-xs text-heda-mute">
          +{group.variants.length - 3} more variants
        </p>
      )}
    </div>
  );
}


export function DefaultCardSingleAddButton({ group, idx, onDirectAdd }) {
  return (
    <div className="mt-auto pt-3">
      <Button
        size="sm"
        className="rounded-xl w-full bg-heda-ink hover:bg-black text-white"
        onClick={() => onDirectAdd(group.variants[0])}
        data-testid={`quick-add-btn-${idx}`}
      >
        <Plus className="w-4 h-4 mr-1" /> Add
      </Button>
    </div>
  );
}


export function DefaultCardMultiVariantButtons({ group, onDirectAdd }) {
  return (
    <div className="mt-auto pt-3 space-y-1">
      {group.variants.slice(0, 3).map((v, i) => (
        <Button
          key={i}
          size="sm"
          variant="outline"
          className="rounded-xl w-full justify-between border-heda-line hover:bg-heda-ink hover:text-white hover:border-heda-ink"
          onClick={() => onDirectAdd(v)}
        >
          <span className="text-sm">
            {v.thickness || 'Add'}
            {v.size && <span className="text-xs ml-1 opacity-70">({v.size})</span>}
          </span>
          <span className="font-mono-data text-sm">{formatINR(v.base_rate)}</span>
        </Button>
      ))}
    </div>
  );
}
